import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Popup } from 'antd-mobile';
import { klona } from 'klona';
import { useSetState } from 'ahooks';
import { pick } from 'lodash-es';
import { storagePrefixV2 } from '@/constant';
import { genStepsBySelectTherapyList } from '../../utils';
import { MySlideRuler } from '../slider-ruler';
import { TPart } from '../select-part';
import { PART_2_LABEL_TEXT } from '../current-dimension';
import { setFormValue, TFormValue, useAIDiagnoseStore } from '../../store/useAIDiagnoseStore';

import cx from './index.module.scss';

interface Props {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  fields: AIDiagnose.DimensionType[];
  setFields: React.Dispatch<React.SetStateAction<AIDiagnose.DimensionType[]>>;
  value: TFormValue['currentDimension'];
  onChange: (value: Partial<Props['value']>) => void;
}

export const DIMENSION_VALUE_CONFIG: Record<
  AIDiagnose.DimensionType,
  {
    default: number;
    min: number;
    max: number;
    label: string;
    previewImg: string;
  }
> = {
  waistCircumference: {
    default: 80,
    min: 40,
    max: 150,
    label: '腰围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/dwqfwqf.png',
  },
  backThickness: {
    default: 30,
    min: 10,
    max: 60,
    label: '背厚',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/wqdqwzsc.png',
  },
  leftArmCircumference: {
    default: 25,
    min: 10,
    max: 60,
    label: '左臂围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/asdqwfwq.png',
  },
  rightArmCircumference: {
    default: 25,
    min: 10,
    max: 60,
    label: '右臂围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/asdasdqw.png',
  },
  leftCalfCircumference: {
    default: 35,
    min: 15,
    max: 100,
    label: '左小腿围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/wqdqwfqg.png',
  },
  rightCalfCircumference: {
    default: 35,
    min: 15,
    max: 100,
    label: '右小腿围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/wewqfg.png',
  },
  leftThighCircumference: {
    default: 50,
    min: 30,
    max: 100,
    label: '左大腿围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/asdqfqfgqwfqwfqw.png',
  },
  rightThighCircumference: {
    default: 50,
    min: 30,
    max: 100,
    label: '右大腿围',
    previewImg: storagePrefixV2 + '/ai-consulation/crumb-bg/wdwqfg.png',
  },
};

const DIMENSION_VALUE_2_TITLE: Record<AIDiagnose.DimensionType, string> = {
  backThickness: '背厚',
  leftArmCircumference: '臂围',
  rightArmCircumference: '臂围',
  leftThighCircumference: '大腿围',
  rightThighCircumference: '大腿围',
  leftCalfCircumference: '小腿围',
  rightCalfCircumference: '小腿围',
  waistCircumference: '腰围',
};

const DIMENSION_VALUE_2_PART: Record<AIDiagnose.DimensionType, TPart> = {
  backThickness: 'backThickness',
  leftArmCircumference: 'armCircumference',
  rightArmCircumference: 'armCircumference',
  leftThighCircumference: 'thighCircumference',
  rightThighCircumference: 'thighCircumference',
  leftCalfCircumference: 'thighCircumference',
  rightCalfCircumference: 'thighCircumference',
  waistCircumference: 'waistCircumference',
};

export const BodyDimensionSelector: React.FC<Props> = ({ fields, setFields, isShow, setIsShow, value, onChange }) => {
  const [currentActiveDimension, setCurrentActiveDimension] = useState<AIDiagnose.DimensionType>(fields[0]);
  const { selectPart, currentDimension, targetDimension, selectTherapyType } = useAIDiagnoseStore.use.formValue();
  const currentStep = useAIDiagnoseStore.use.currentStep();
  const [valueObj, setValueObj] = useSetState<Partial<Record<AIDiagnose.DimensionType, number>>>({});
  const lastClickConfirmTime = useRef(null);

  const steps = useMemo(() => genStepsBySelectTherapyList(selectTherapyType), [selectTherapyType]);

  const currentStepIsTargetDimension = useMemo(() => steps[currentStep] === 'targetDimension', [currentStep, steps]);

  const currentSelectPartWithSort = useMemo(
    () =>
      klona(selectPart)?.sort(
        (a, b) => Object.keys(PART_2_LABEL_TEXT)?.indexOf(a) - Object.keys(PART_2_LABEL_TEXT)?.indexOf(b)
      ),
    [selectPart]
  );

  const currentPickIndex = useMemo(
    () => currentSelectPartWithSort?.findIndex(item => item === DIMENSION_VALUE_2_PART[fields?.[0]]),
    [currentSelectPartWithSort, fields]
  );

  useEffect(() => {
    if (isShow) {
      setTimeout(() => fields?.[0] && setCurrentActiveDimension(fields[0]), 100);
    }
  }, [currentDimension, fields, isShow]);

  return (
    <Popup
      getContainer={null}
      bodyClassName={cx('BodyDimensionSelector')}
      visible={isShow}
      onMaskClick={() => {
        setIsShow(false);
      }}
      destroyOnClose={true}
      onClose={() => {
        setIsShow(false);
      }}
    >
      <div className={cx('title')}>
        录入您的{currentStepIsTargetDimension ? '目标' : '当前'}
        {DIMENSION_VALUE_2_TITLE[fields?.[0]]}
      </div>

      <div className={cx('content')}>
        <div className={cx('preview-img')}>
          <img src={DIMENSION_VALUE_CONFIG[currentActiveDimension]?.previewImg} alt="" />
        </div>
        <div className={cx('slide-ruler-box')}>
          {fields?.map(item => (
            <div className={cx('slide-ruler-item')} key={item}>
              <div className={cx('info')}>
                <div className={cx('label')}>
                  {currentStepIsTargetDimension ? '目标' : '当前'}
                  {DIMENSION_VALUE_CONFIG[item].label}
                </div>
                <div className={cx('value')}>
                  <span className={cx('value-text')}>{valueObj[item]}</span>
                  <span>厘米</span>
                </div>
              </div>
              <MySlideRuler
                isShow={isShow}
                value={currentStepIsTargetDimension ? targetDimension?.[item] : currentDimension?.[item]}
                defaultValue={DIMENSION_VALUE_CONFIG[item]?.default}
                min={DIMENSION_VALUE_CONFIG[item].min}
                max={currentStepIsTargetDimension ? currentDimension[item] : DIMENSION_VALUE_CONFIG[item].max}
                onChange={value => {
                  setCurrentActiveDimension(item);
                  setValueObj({ [item]: value });
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className={cx('footer')}>
        <Button
          className={cx('btn')}
          color="primary"
          onClick={() => {
            if (lastClickConfirmTime.current && Date.now() - lastClickConfirmTime.current < 500) {
              return;
            }

            lastClickConfirmTime.current = Date.now();

            // 更改当前围度的时候，清除目前围度的值
            if (!currentStepIsTargetDimension) {
              fields?.forEach(item => (targetDimension[item] = 0));
              setFormValue(
                {
                  targetDimension,
                },
                { isMerge: true }
              );
            }
            onChange(pick(valueObj, fields));

            // 点击确认时，自动跳转到下一设置值中
            if (currentPickIndex < currentSelectPartWithSort?.length - 1) {
              setFields(PART_2_LABEL_TEXT[currentSelectPartWithSort[currentPickIndex + 1]]?.partFields);
            }
            // 当前填写的是大腿，需调整到小腿值设置
            else if (fields?.[0] === 'leftThighCircumference' || fields?.[1] === 'leftThighCircumference') {
              setFields(['leftCalfCircumference', 'rightCalfCircumference']);
            } else {
              setIsShow(false);
            }
          }}
        >
          确定
        </Button>
      </div>
    </Popup>
  );
};
